import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { TbUserEdit, TbUserPlus } from "react-icons/tb";
import * as yup from "yup";
import api from "../../../Services/api";
import { useToast } from "../../../Hooks/toast";
import { ValutDialog } from "../../../Components/Molecules/ValutDialog";
import { ValutField, ValutInput, ValutSelect } from "../../../Components/Atoms/ValutInput";
import { PermissionConfiguration } from "./OperatorForm/components/PermissionConfiguration";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  onSaved?: () => void;
  operatorId?: string;
  defaultProfile?: "admin" | "user" | "subAccount";
  defaultSubAccountId?: string;
};

const validationSchema = yup.object().shape({
  name: yup.string().required("O campo Nome é obrigatório."),
  email: yup
    .string()
    .email("Insira um e-mail válido.")
    .required("O campo E-mail é obrigatório."),
  phone: yup
    .string()
    .min(11, "Telefone deve conter 11 dígitos")
    .required("O campo Telefone é obrigatório."),
  profile: yup.string().required("O campo Tipo de usuário é obrigatório."),
});

const initialOperator = {
  name: "",
  email: "",
  phone: "",
  profile: "admin",
  subAccountId: "",
  isActive: true,
  permissions: [] as string[],
  ranges: {} as Record<string, unknown>,
};

const PROFILE_OPTIONS = [
  { value: "admin", label: "Administrador" },
  { value: "user", label: "Usuário" },
  { value: "subAccount", label: "Operador" },
];

const inputErrorClass = (hasError: boolean) =>
  hasError ? "!border-red-400 focus:!border-red-400 focus:!ring-red-400/20" : "";

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="mt-1 text-xs text-red-500">{message}</p> : null;

const CreateOperatorModal = ({
  open,
  onClose,
  onCreated,
  onSaved,
  operatorId,
  defaultProfile,
  defaultSubAccountId,
}: Props) => {
  const { addToast } = useToast();

  const [operator, setOperator] = useState({ ...initialOperator });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [subAccounts, setSubAccounts] = useState<{ subAccountId: string; name: string }[]>([]);
  const [permissions, setPermissions] = useState<PermissionGroup[]>([]);
  const [saving, setSaving] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setOperator((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const resetState = () => {
    setOperator({ ...initialOperator });
    setErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        // GET /subaccount is not available on MobileService — skip; PDV select stays empty.
        const permissionsRes = await api.get("operators/permissions/Groupped");
        setPermissions(permissionsRes.data || []);
        setSubAccounts([]);

        if (operatorId) {
          const { data } = await api.get(`operators/${operatorId}`);
          setOperator({
            name: data.name || "",
            email: data.email || "",
            phone: data.phone || "",
            profile: data.profile || "admin",
            subAccountId: data.subAccountId || "",
            isActive: !!data.isActive,
            permissions: data.permissions || [],
            ranges: data.ranges || {},
          });
        } else {
          setOperator({
            ...initialOperator,
            profile: defaultProfile || "admin",
            subAccountId: defaultSubAccountId || "",
          });
        }
      } catch {
        // ignore
      }
    };

    load();
    try {
      window.dispatchEvent(new CustomEvent("app-tooltip-close-all"));
    } catch {
      // ignore
    }
  }, [open, operatorId, defaultProfile, defaultSubAccountId]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await validationSchema.validate(operator, { abortEarly: false });

      const body: Record<string, unknown> = { ...operator, role: operator.profile };
      if (!body.subAccountId) body.subAccountId = null;

      if (operatorId) {
        await api.put(`/operators/${operatorId}`, body);
      } else {
        await api.post("/operators", body);
      }

      setErrors({});
      addToast({ title: "Sucesso", description: "Operador salvo com sucesso", type: "success" });

      onSaved?.();
      onCreated?.();
      resetState();
      onClose();
    } catch (err: unknown) {
      const validationErr = err as { name?: string; inner?: { path?: string; message?: string }[] };
      if (validationErr?.name === "ValidationError") {
        const newErrors: Record<string, string> = {};
        validationErr.inner?.forEach((inner) => {
          if (inner?.path) newErrors[inner.path] = inner.message || "";
        });
        setErrors(newErrors);
      } else {
        addToast({ title: "Erro", description: "Erro ao salvar o operador", type: "error" });
      }
    } finally {
      setSaving(false);
    }
  };

  const showPosField =
    operator.profile === "subAccount" ||
    (!!operator.subAccountId &&
      !/^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(operator.subAccountId));

  const isEditing = Boolean(operatorId);

  return (
    <ValutDialog
      open={open}
      onClose={handleClose}
      title={isEditing ? "Editar operador" : "Novo operador"}
      titleId="create-operator-title"
      icon={
        isEditing ? (
          <TbUserEdit className="h-4 w-4" />
        ) : (
          <TbUserPlus className="h-4 w-4" />
        )
      }
      maxWidth={operator.profile === "user" ? "lg" : "sm"}
      footer={
        <button
          form="frmCreateOperator"
          type="submit"
          className="btn-primary w-full sm:w-auto"
          disabled={saving}
        >
          {saving ? "Salvando..." : "Salvar"}
        </button>
      }
    >
      <form id="frmCreateOperator" onSubmit={onSubmit} className="space-y-4">
        <ValutField label="Nome" htmlFor="operator-name">
          <ValutInput
            id="operator-name"
            name="name"
            value={operator.name}
            onChange={handleChange}
            className={inputErrorClass(!!errors.name)}
            autoFocus
          />
          <FieldError message={errors.name} />
        </ValutField>

        <ValutField label="E-mail" htmlFor="operator-email">
          <ValutInput
            id="operator-email"
            name="email"
            type="email"
            value={operator.email}
            onChange={handleChange}
            className={inputErrorClass(!!errors.email)}
          />
          <FieldError message={errors.email} />
        </ValutField>

        <ValutField label="Telefone" htmlFor="operator-phone">
          <ValutInput
            id="operator-phone"
            name="phone"
            type="tel"
            value={operator.phone}
            onChange={handleChange}
            placeholder="11999999999"
            className={inputErrorClass(!!errors.phone)}
          />
          <FieldError message={errors.phone} />
        </ValutField>

        <ValutField label="Tipo de usuário" htmlFor="operator-profile">
          <ValutSelect
            id="operator-profile"
            name="profile"
            value={operator.profile}
            onChange={handleChange}
            options={PROFILE_OPTIONS}
            className={inputErrorClass(!!errors.profile)}
          />
          <FieldError message={errors.profile} />
        </ValutField>

        {isEditing ? (
          <FormControlLabel
            className="!mx-0 !items-start"
            checked={operator.isActive}
            onChange={() => setOperator((o) => ({ ...o, isActive: !o.isActive }))}
            control={
              <Checkbox
                size="small"
                color="primary"
                className="!mt-0.5"
              />
            }
            label={
              <span className="text-sm text-brand-dark dark:text-gray-100">
                Operador ativo
              </span>
            }
          />
        ) : null}

        {showPosField ? (
          <ValutField label="Ponto de venda" htmlFor="operator-subaccount">
            <ValutSelect
              id="operator-subaccount"
              name="subAccountId"
              value={operator.subAccountId}
              onChange={handleChange}
              options={subAccounts.map((subAccount) => ({
                value: subAccount.subAccountId,
                label: `${subAccount.name} - ${subAccount.subAccountId}`,
              }))}
              className={inputErrorClass(!!errors.subAccountId)}
            />
            <FieldError message={errors.subAccountId} />
          </ValutField>
        ) : null}

        {operator.profile === "user" ? (
          <div className="pt-1">
            <PermissionConfiguration
              permissionGroups={permissions}
              onChange={(perms) => setOperator((o) => ({ ...o, permissions: perms }))}
              userPermissions={operator.permissions}
            />
          </div>
        ) : null}
      </form>
    </ValutDialog>
  );
};

export default CreateOperatorModal;
